import React, { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import classNames from 'classnames';
import { PasswordInput } from '@/components/PasswordInput';
import { ResetPasswordPopup } from '@/components/ResetPasswordPopup';
import { apiClient } from '@/services/api';
import { useTranslation } from 'react-i18next';
import styles from './styles.module.scss';

interface ChangePasswordFormData {
  passwordOld: string;
  passwordNew: string;
  passwordConfirm: string;
}

export const ChangePassword: React.FC = () => {
  const { t } = useTranslation(['account', 'common']);
  const [showForm, setShowForm] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const { handleSubmit, watch, reset, control } = useForm<ChangePasswordFormData>({
    defaultValues: {
      passwordOld: '',
      passwordNew: '',
      passwordConfirm: '',
    },
  });

  const openForm = () => {
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    reset();
  };

  const onValidationError = (errors: Record<string, any>) => {
    const firstError = Object.values(errors)[0];
    const message = firstError?.message || t('account:fillRequiredFields');
    toast.error(message);
  };

  const onSubmit = async (data: ChangePasswordFormData) => {
    setIsLoading(true);
    try {
      const response = await apiClient.changePassword(data.passwordOld, data.passwordNew);
      if (response.data.success) {
        toast.success(t('account:passwordChanged'));
        closeForm();
      }
    } catch (err: any) {
      const message = err.response?.data?.message || t('account:errorChangingPassword');
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetPassword = (e: React.MouseEvent) => {
    e.preventDefault();
    setResetOpen(true);
  };

  return (
    <div className={classNames('card', styles.card)}>
      <div className={styles.header}>
        <div className="d-flex flex-column g-8">
          <h3>{t('account:changePassword')}</h3>
          <p>{t('account:changeOrResetPassword')}</p>
        </div>
        {!showForm ? (
          <button
            className={styles.iconBtn}
            onClick={openForm}
            aria-label={t('account:editPassword')}
            type="button"
          >
            <i className="icon-pen" />
          </button>
        ) : (
          <button
            className={styles.iconBtn}
            onClick={closeForm}
            aria-label={t('common:close')}
            type="button"
          >
            <i className="icon-cross" />
          </button>
        )}
      </div>

      {!showForm && (
        <button
          className="text-button text-button--sm"
          onClick={handleResetPassword}
          style={{ alignSelf: 'flex-start' }}
          type="button"
        >
          {t('account:resetPassword')}
        </button>
      )}

      {showForm && (
        <form className="d-flex flex-column g-20" onSubmit={handleSubmit(onSubmit, onValidationError)}>
          <Controller
            control={control}
            name="passwordOld"
            rules={{ required: t('account:oldPasswordRequired') }}
            render={({ field: { ref, onChange, value }, fieldState: { error } }) => (
              <PasswordInput
                className={classNames('field', { 'field--error': error })}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                placeholder={t('account:oldPassword')}
                innerRef={ref}
                disabled={isLoading}
              />
            )}
          />
          <Controller
            control={control}
            name="passwordNew"
            rules={{ required: t('account:newPasswordRequired'), minLength: { value: 6, message: t('account:passwordMinChars') } }}
            render={({ field: { ref, onChange, value }, fieldState: { error } }) => (
              <PasswordInput
                className={classNames('field', { 'field--error': error })}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                placeholder={t('account:newPasswordMinChars')}
                innerRef={ref}
                disabled={isLoading}
              />
            )}
          />
          <Controller
            control={control}
            name="passwordConfirm"
            rules={{
              required: t('account:confirmNewPasswordRequired'),
              validate: (value) => value === watch('passwordNew') || t('account:passwordsDoNotMatch'),
            }}
            render={({ field: { ref, onChange, value }, fieldState: { error } }) => (
              <PasswordInput
                className={classNames('field', { 'field--error': error })}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                placeholder={t('account:confirmNewPassword')}
                innerRef={ref}
                disabled={isLoading}
              />
            )}
          />
          <div className={styles.actions}>
            <button
              className="btn btn--sm btn--outlined"
              type="button"
              onClick={closeForm}
              disabled={isLoading}
            >
              {t('common:cancel')}
            </button>
            <button
              className="btn btn--sm"
              type="submit"
              disabled={isLoading}
            >
              {isLoading ? t('account:changing') : t('account:changePassword')}
            </button>
          </div>
        </form>
      )}
      <ResetPasswordPopup open={resetOpen} onClose={() => setResetOpen(false)} />
    </div>
  );
};

export default ChangePassword;
